import * as THREE from 'three'
import type {RawAnchor, RawAnchorAsset} from '../api/types'
import {buildAsset} from './assets'
import {num, transformFromAsset} from './mapping'

interface AnimatedChild {
  holder: THREE.Object3D
  spinRadPerSec: number
  billboard: boolean
}

export interface AnchorGroup {
  group: THREE.Group
  /** Advance spin/billboard animations. */
  tick: (dtSeconds: number, cameraWorldPos: THREE.Vector3) => void
  dispose: () => void
  loadedCount: number
  skippedCount: number
}

/**
 * Build the full renderable content of one anchor from its anchor_assets,
 * applying the Unity→three transform mapping per asset. Loads concurrently;
 * resolves when every asset has either loaded, degraded to a placeholder,
 * or been skipped (audio/video).
 */
export async function buildAnchorGroup(
  anchor: RawAnchor,
  onProgress?: (loaded: number, total: number) => void,
): Promise<AnchorGroup> {
  const group = new THREE.Group()
  group.name = `anchor-${anchor.id}`
  const animated: AnimatedChild[] = []
  const disposers: Array<() => void> = []

  const configs = [...(anchor.anchor_assets ?? [])].sort(
    (a, b) => (num(a.order) ?? 0) - (num(b.order) ?? 0),
  )

  let loaded = 0
  let skipped = 0
  const total = configs.length

  await Promise.all(
    configs.map(async (cfg: RawAnchorAsset) => {
      const built = await buildAsset(cfg)
      loaded += 1
      onProgress?.(loaded, total)
      if (!built) {
        skipped += 1
        return
      }
      const t = transformFromAsset(cfg)
      // Holder carries the authored transform; the asset object stays pristine
      // inside it so billboard/spin never fight the authored yaw.
      const holder = new THREE.Object3D()
      holder.position.set(...t.position)
      holder.rotation.y = t.yawRad
      holder.scale.setScalar(t.scale)
      holder.add(built.object)
      group.add(holder)
      disposers.push(built.dispose)
      if (t.spinRadPerSec !== 0 || t.billboard) {
        animated.push({holder, spinRadPerSec: t.spinRadPerSec, billboard: t.billboard})
      }
    }),
  )

  const worldPos = new THREE.Vector3()
  const parentQuat = new THREE.Quaternion()
  const parentFwd = new THREE.Vector3()
  const tick = (dt: number, cameraWorldPos: THREE.Vector3) => {
    for (const a of animated) {
      if (a.billboard) {
        // Yaw-only billboard: face the camera horizontally, keep upright.
        a.holder.getWorldPosition(worldPos)
        const yaw = Math.atan2(cameraWorldPos.x - worldPos.x, cameraWorldPos.z - worldPos.z)
        // Convert the desired WORLD yaw into the holder's local frame using the
        // full ancestor rotation — in the quest the chain includes the rotated
        // geo worldGroup (rotation.y = θ), so `group.rotation.y` alone is wrong.
        a.holder.parent!.getWorldQuaternion(parentQuat)
        parentFwd.set(0, 0, 1).applyQuaternion(parentQuat)
        const parentYaw = Math.atan2(parentFwd.x, parentFwd.z)
        a.holder.rotation.y = yaw - parentYaw
      }
      if (a.spinRadPerSec !== 0) {
        a.holder.rotation.y += a.spinRadPerSec * dt
      }
    }
  }

  return {
    group,
    tick,
    dispose: () => disposers.forEach((d) => d()),
    loadedCount: total - skipped,
    skippedCount: skipped,
  }
}
